import { appAPI, ApiTags, ApiMethods } from '.';

interface ContentfulAppsResponse {
  items: Array<any>;
  includes: {
    Asset: Array<any>;
  };
}

export enum ContentfulAppType {
  Internal = 'Internal',
  Client = 'Client',
}

export enum ContentfulAppCreator {
  Copilot = 'Copilot',
  Partner = 'Partner',
}

export interface ContentfulApp {
  name: string; // name of the app
  description: string; // description of the app
  iconUrl: string; // url of the icon
  appsType: ContentfulAppType; // will this app be displayed internal or client side
  appCreator: ContentfulAppCreator; // this is used to display "Made by Copilot" badge
  learnMoreLink: string; // url to the setup instructions page
  copilotAppId: string; // core module id
  isFeatured: boolean;
  isPlatformApp: boolean;
  releaseSegment?: 'Launchpad' | 'Dogfood';
  presetName?: string;
}

export const contentfulApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    getContentfulApps: build.query<ContentfulApp[], void>({
      query: () => ({
        path: '/data/contentful',
        method: ApiMethods.get,
        options: {
          queryStringParameters: {
            select: 'fields',
            contentType: 'partnerApps',
          },
        },
      }),
      providesTags: [ApiTags.contentfulApps],
      // formatting the api response
      transformResponse(baseQueryReturnValue: ContentfulAppsResponse) {
        if (!baseQueryReturnValue) return [];

        const allApps = baseQueryReturnValue.items; // all apps added in Contentful CMS
        const allAssets = baseQueryReturnValue.includes.Asset; // all asset files

        return allApps.map((app) => {
          const {
            name,
            appCreator,
            appsType,
            description,
            isFeatured,
            setupInstructionsLink,
            copilotAppId,
            icon,
            isPlatformApp,
            releaseSegment,
            presetName,
          } = app.fields;

          // get icon information from all assets array
          const iconInfo = allAssets.find(
            (asset) => asset.sys.id === icon?.sys.id,
          );
          // url to access the app icon
          const iconUrl = iconInfo?.fields?.file?.url || '';

          return {
            name,
            description,
            iconUrl,
            appsType,
            appCreator,
            learnMoreLink: setupInstructionsLink,
            copilotAppId,
            isFeatured,
            isPlatformApp,
            releaseSegment,
            presetName,
          };
        });
      },
    }),
  }),
});

export const { useGetContentfulAppsQuery } = contentfulApi;
